<!-- ModalEntregaPedido.vue — Acciones posteriores a la entrega de un pedido -->
<!--
  Factura, guías, notas y proyectos de un pedido ya entregado.
  Se separó de ModalEditarItems.vue porque ese modal es para *editar* el pedido
  (artículos, prioridad) y este es para *cerrarlo*: mandar documentos al cliente
  y dejar constancia de si le llegaron.

  El estado de WhatsApp que se muestra aquí es el real, reportado por Meta vía
  webhook — no el "se encoló correctamente" de nuestra propia petición.
-->
<template>
  <div class="modal fade" id="modalEntregaPedido" tabindex="-1" ref="el">
    <div class="modal-dialog modal-xl modal-dialog-scrollable entrega-modal-dialog">
      <div class="modal-content entrega-modal border-0">
        <!-- ── Header ─────────────────────────────────────────── -->
        <div class="modal-header border-0 pb-0">
          <div>
            <span class="fw-bold text-cafe titulo-modal">
              Entrega — Pedido #{{ pedido?.numero_pedido }}
            </span>
            <small class="text-verde fw-bold d-block cliente-nombre">
              <span class="badge-cliente-num me-1">{{ pedido?.cliente_numero }}</span>
              {{ pedido?.cliente }}
              <span class="tel-chip" :class="tieneTelefono ? 'ok' : 'falta'">
                <i class="bi" :class="tieneTelefono ? 'bi-telephone-fill' : 'bi-telephone-x'"></i>
                {{ pedido?.cliente_telefono || "Sin teléfono" }}
              </span>
            </small>
          </div>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body pt-2">
          <!-- Sin teléfono no hay nada que mandar: se avisa una sola vez arriba -->
          <div v-if="!tieneTelefono" class="aviso-sin-telefono">
            <i class="bi bi-exclamation-triangle-fill me-2"></i>
            <div>
              <strong>Este cliente no tiene teléfono registrado.</strong>
              <span class="d-block">
                Sin teléfono no se puede mandar la factura ni las guías. Regístralo
                en el catálogo de clientes y vuelve a abrir esta ventana.
              </span>
            </div>
          </div>

          <!-- ── Pestañas ─────────────────────────────────────── -->
          <ul class="nav-tabs-custom mt-1 mb-3">
            <li v-for="tab in pestanas" :key="tab.key">
              <button
                class="tab-btn"
                :class="{ active: pestanaActiva === tab.key }"
                @click="pestanaActiva = tab.key"
              >
                <i :class="tab.icon + ' me-1'"></i>{{ tab.label }}
                <span v-if="tab.contador" class="tab-contador">{{ tab.contador }}</span>
              </button>
            </li>
          </ul>

          <!-- ── Pestaña: Factura ───────────────────────────────── -->
          <div v-show="pestanaActiva === 'factura'">
            <div class="bloque">
              <div class="bloque-header">
                <i class="bi bi-file-earmark-pdf text-danger"></i>
                <span>Factura del pedido</span>
              </div>

              <div class="accion-row">
                <div class="archivo-zona-wrap">
                  <input
                    ref="inputFactura"
                    type="file"
                    accept="application/pdf"
                    class="d-none"
                    @change="onSeleccionarFactura"
                  />
                  <div class="archivo-zona" @click="inputFactura.click()">
                    <template v-if="facturaArchivo">
                      <i class="bi bi-file-earmark-check text-success me-1"></i>
                      {{ facturaArchivo.name }}
                    </template>
                    <template v-else-if="pedido?.factura_pdf">
                      <i class="bi bi-file-earmark-pdf text-danger me-1"></i>
                      <a :href="pedido.factura_pdf" target="_blank" @click.stop>Ver factura</a>
                      <span class="ms-2 text-muted small">(Cambiar)</span>
                    </template>
                    <template v-else>
                      <i class="bi bi-upload me-1"></i>Seleccionar PDF…
                    </template>
                  </div>
                </div>

                <button
                  type="button"
                  class="btn-accion btn-wa"
                  :disabled="!puedeMandarFactura"
                  :title="motivoBloqueoFactura"
                  @click="handleEnviarFactura"
                >
                  <span v-if="enviandoFactura" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="bi bi-whatsapp me-1"></i>
                  {{ textoBotonFactura }}
                </button>
              </div>

              <!-- Estado real reportado por Meta -->
              <EstatusWhatsapp :mensaje="ultimaFactura" />

              <p v-if="!ultimaFactura && !errorFactura" class="sin-envios-hint">
                Todavía no se ha mandado la factura de este pedido.
              </p>
              <div v-if="errorFactura" class="error-local">
                <i class="bi bi-exclamation-triangle-fill me-2"></i>{{ errorFactura }}
              </div>
            </div>

            <!-- Historial de intentos previos -->
            <HistorialEnvios :mensajes="historialFactura" titulo="Envíos anteriores de la factura" />
          </div>

          <!-- ── Pestaña: Guías ─────────────────────────────────── -->
          <div v-show="pestanaActiva === 'guias'">
            <div class="bloque">
              <div class="bloque-header">
                <i class="bi bi-truck"></i>
                <span>Guías de envío</span>
                <span v-if="guias.length" class="badge-count">{{ guias.length }}</span>
              </div>

              <div v-if="guias.length > 0" class="guias-lista">
                <div v-for="guia in guias" :key="guia.id" class="guia-row">
                  <span class="guia-badge-paq">{{ guia.paqueteria_nombre }}</span>
                  <span class="guia-num">{{ guia.numero_guia }}</span>
                  <button
                    type="button"
                    class="btn-quitar"
                    :disabled="eliminandoGuia === guia.id"
                    title="Eliminar guía"
                    @click="handleEliminarGuia(guia.id)"
                  >
                    <span
                      v-if="eliminandoGuia === guia.id"
                      class="spinner-border spinner-border-sm"
                    ></span>
                    <i v-else class="bi bi-trash3"></i>
                  </button>
                </div>
              </div>
              <p v-else class="sin-envios-hint">Sin guías registradas aún.</p>

              <!-- Alta de guía -->
              <div class="nueva-guia-wrap">
                <div class="select-wrapper" v-click-outside="cerrarDropdownPaq">
                  <div
                    class="paq-display"
                    :class="{ placeholder: !nuevaPaqueteria }"
                    @click="abiertoPaq = !abiertoPaq"
                  >
                    {{ nuevaPaqueteria?.nombre || "Paquetería" }}
                    <i class="bi" :class="abiertoPaq ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                  </div>
                  <div v-if="abiertoPaq" class="paq-dropdown">
                    <div v-if="cargandoPaqueterias" class="paq-item text-muted small">
                      <span class="spinner-border spinner-border-sm me-1"></span>Cargando...
                    </div>
                    <div
                      v-for="paq in paqueterias"
                      :key="paq.id"
                      class="paq-item"
                      :class="{ selected: nuevaPaqueteria?.id === paq.id }"
                      @mousedown.prevent="seleccionarPaqueteria(paq)"
                    >
                      {{ paq.nombre }}
                    </div>
                    <div
                      v-if="!cargandoPaqueterias && paqueterias.length === 0"
                      class="paq-item text-muted small"
                    >
                      Sin paqueterías disponibles
                    </div>
                  </div>
                </div>
                <input
                  v-model="nuevoNumeroGuia"
                  type="text"
                  class="guia-input"
                  placeholder="Número de guía"
                  @keyup.enter="handleAgregarGuia"
                />
                <button
                  type="button"
                  class="btn-accion btn-agregar"
                  :disabled="!nuevaPaqueteria || !nuevoNumeroGuia.trim() || agregandoGuia"
                  @click="handleAgregarGuia"
                >
                  <span v-if="agregandoGuia" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="bi bi-plus-lg me-1"></i>Agregar
                </button>
              </div>

              <!-- Envío por WhatsApp -->
              <div class="accion-row mt-2">
                <div class="requisitos">
                  <span class="requisito" :class="tieneTelefono ? 'ok' : 'falta'">
                    <i class="bi" :class="tieneTelefono ? 'bi-check-circle-fill' : 'bi-x-circle-fill'"></i>
                    Teléfono
                  </span>
                  <span class="requisito" :class="guias.length ? 'ok' : 'falta'">
                    <i class="bi" :class="guias.length ? 'bi-check-circle-fill' : 'bi-x-circle-fill'"></i>
                    Guía registrada
                  </span>
                </div>
                <button
                  type="button"
                  class="btn-accion btn-wa"
                  :disabled="!puedeNotificar"
                  :title="motivoBloqueoGuias"
                  @click="handleNotificarEnvio"
                >
                  <span
                    v-if="enviandoNotificacion"
                    class="spinner-border spinner-border-sm me-1"
                  ></span>
                  <i v-else class="bi bi-whatsapp me-1"></i>
                  {{ textoBotonGuias }}
                </button>
              </div>

              <EstatusWhatsapp :mensaje="ultimasGuias" />

              <div v-if="errorNotificacion" class="error-local">
                <i class="bi bi-exclamation-triangle-fill me-2"></i>{{ errorNotificacion }}
              </div>
            </div>

            <HistorialEnvios :mensajes="historialGuias" titulo="Envíos anteriores de guías" />
          </div>

          <!-- ── Pestaña: Notas ─────────────────────────────────── -->
          <div v-show="pestanaActiva === 'notas'">
            <div class="bloque">
              <div class="bloque-header">
                <i class="bi bi-chat-left-text"></i>
                <span>Notas del pedido</span>
              </div>
              <textarea
                v-model="notas"
                class="notas-textarea"
                rows="6"
                placeholder="Observaciones, incidencias de la entrega, comentarios internos..."
              ></textarea>
              <div class="d-flex justify-content-between align-items-center mt-2">
                <small class="hint">
                  <i class="bi bi-info-circle me-1"></i>
                  Visibles internamente para el equipo.
                </small>
                <button
                  type="button"
                  class="btn-accion btn-guardar"
                  :disabled="guardandoNotas || !notasCambiaron"
                  @click="handleGuardarNotas"
                >
                  <span v-if="guardandoNotas" class="spinner-border spinner-border-sm me-1"></span>
                  <i v-else class="bi bi-floppy me-1"></i>
                  {{ guardandoNotas ? "Guardando..." : "Guardar notas" }}
                </button>
              </div>
            </div>
          </div>

          <!-- ── Pestaña: Proyectos ─────────────────────────────── -->
          <div v-show="pestanaActiva === 'proyectos'">
            <div
              class="upload-zone mb-3"
              :class="{ dragging: arrastrando }"
              @click="inputProyectos.click()"
              @dragover.prevent="arrastrando = true"
              @dragleave.prevent="arrastrando = false"
              @drop.prevent="handleDropProyectos"
            >
              <input
                ref="inputProyectos"
                type="file"
                accept="application/pdf"
                multiple
                class="d-none"
                @change="handleChangeProyectos"
              />
              <div class="upload-inner">
                <i class="bi bi-file-earmark-plus upload-icon"></i>
                <div>
                  <p class="fw-bold text-cafe mb-0">Arrastra PDFs de proyecto aquí</p>
                  <small class="text-muted">
                    o haz clic para seleccionar · puedes subir varios a la vez
                  </small>
                </div>
              </div>
            </div>

            <div
              v-if="proyectosGuardados.length === 0 && proyectosNuevos.length === 0"
              class="vacio-container"
            >
              <i class="bi bi-file-earmark-pdf"></i>
              <p class="mt-2 mb-0">Sin proyectos adjuntos</p>
              <small>Sube los PDFs para que el equipo pueda consultarlos</small>
            </div>

            <div v-else class="proyectos-lista">
              <template v-if="proyectosGuardados.length > 0">
                <small class="text-cafe fw-bold d-block mb-2">
                  <i class="bi bi-cloud-check me-1 text-verde"></i>
                  {{ proyectosGuardados.length }} proyecto(s) guardado(s)
                </small>
                <div v-for="p in proyectosGuardados" :key="'g-' + p.id" class="proyecto-card">
                  <div class="proyecto-header">
                    <div class="proyecto-meta">
                      <i class="bi bi-file-earmark-pdf-fill me-2 text-danger"></i>
                      <span class="proyecto-nombre">{{ nombreArchivo(p.archivo) }}</span>
                    </div>
                    <div class="d-flex align-items-center gap-2">
                      <button class="btn-toggle-preview" @click="p._preview = !p._preview">
                        <i class="bi" :class="p._preview ? 'bi-eye-slash' : 'bi-eye'"></i>
                        {{ p._preview ? "Ocultar" : "Ver" }}
                      </button>
                      <button
                        class="btn-quitar"
                        :disabled="eliminandoProyecto === p.id"
                        title="Eliminar proyecto"
                        @click="handleEliminarProyecto(p.id)"
                      >
                        <span
                          v-if="eliminandoProyecto === p.id"
                          class="spinner-border spinner-border-sm"
                        ></span>
                        <i v-else class="bi bi-trash3"></i>
                      </button>
                    </div>
                  </div>
                  <div v-if="p._preview" class="preview-container">
                    <embed :src="p.archivo" type="application/pdf" class="pdf-embed" />
                  </div>
                </div>
              </template>

              <template v-if="proyectosNuevos.length > 0">
                <small class="text-cafe fw-bold d-block mb-2 mt-3">
                  <i class="bi bi-clock-history me-1"></i>
                  {{ proyectosNuevos.length }} pendiente(s) de guardar
                </small>
                <div
                  v-for="(p, idx) in proyectosNuevos"
                  :key="'n-' + idx"
                  class="proyecto-card proyecto-card-nuevo"
                >
                  <div class="proyecto-header">
                    <div class="proyecto-meta">
                      <i class="bi bi-file-earmark-pdf-fill me-2 text-danger"></i>
                      <span class="proyecto-nombre">{{ p.archivo.name }}</span>
                      <span class="proyecto-size text-muted ms-2">
                        ({{ formatSize(p.archivo.size) }})
                      </span>
                    </div>
                    <button class="btn-quitar" title="Quitar" @click="quitarProyectoNuevo(idx)">
                      <i class="bi bi-x-lg"></i>
                    </button>
                  </div>
                </div>
                <button
                  class="btn-subir-proyectos mt-2"
                  :disabled="subiendoProyectos"
                  @click="handleSubirProyectos"
                >
                  <span
                    v-if="subiendoProyectos"
                    class="spinner-border spinner-border-sm me-2"
                  ></span>
                  <i v-else class="bi bi-cloud-upload me-2"></i>
                  {{
                    subiendoProyectos
                      ? "Subiendo..."
                      : `Guardar ${proyectosNuevos.length} proyecto(s)`
                  }}
                </button>
              </template>
            </div>
          </div>
        </div>

        <!-- ── Footer ─────────────────────────────────────────── -->
        <div class="modal-footer border-0 pt-0">
          <small class="conexion-hint me-auto">
            <i class="bi bi-broadcast me-1"></i>
            El estado de los mensajes se actualiza solo conforme WhatsApp lo reporte.
          </small>
          <button class="btn btn-cerrar" data-bs-dismiss="modal">Cerrar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";
import EstatusWhatsapp from "./EstatusWhatsapp.vue";
import HistorialEnvios from "./HistorialEnvios.vue";
import { useMensajesWhatsapp, enTransito, fallo } from "./composables/useMensajesWhatsapp.js";
import {
  subirFacturaPedido,
  enviarFacturaPedido,
  notificarEnvioPedido,
  getPaqueterias,
  agregarGuiaPedido,
  eliminarGuiaPedido,
  agregarProyectoPedido,
  eliminarProyectoPedido,
  actualizarPedido,
} from "@/utils/api_backend.js";

const emit = defineEmits(["guardado"]);

const el = ref(null);
let modalInstance = null;

const pedido = ref(null);
const pestanaActiva = ref("factura");

// ── Estado de WhatsApp (histórico + WebSocket) ─────────────
const { mensajes, cargar: cargarMensajes, limpiar: limpiarMensajes, ultimaFactura, ultimasGuias } =
  useMensajesWhatsapp();

// El más reciente se muestra arriba; los anteriores van al historial.
const historialFactura = computed(() =>
  mensajes.value.filter((m) => m.tipo === "FACTURA").slice(1)
);
const historialGuias = computed(() =>
  mensajes.value.filter((m) => m.tipo === "GUIAS").slice(1)
);

// ── Factura ────────────────────────────────────────────────
const inputFactura = ref(null);
const facturaArchivo = ref(null);
const enviandoFactura = ref(false);
const errorFactura = ref("");

// ── Guías ──────────────────────────────────────────────────
const guias = ref([]);
const paqueterias = ref([]);
const cargandoPaqueterias = ref(false);
const abiertoPaq = ref(false);
const nuevaPaqueteria = ref(null);
const nuevoNumeroGuia = ref("");
const agregandoGuia = ref(false);
const eliminandoGuia = ref(null);
const enviandoNotificacion = ref(false);
const errorNotificacion = ref("");

// ── Notas ──────────────────────────────────────────────────
const notas = ref("");
const notasOriginales = ref("");
const guardandoNotas = ref(false);
const notasCambiaron = computed(() => notas.value !== notasOriginales.value);

// ── Proyectos ──────────────────────────────────────────────
const inputProyectos = ref(null);
const arrastrando = ref(false);
const proyectosGuardados = ref([]);
const proyectosNuevos = ref([]);
const subiendoProyectos = ref(false);
const eliminandoProyecto = ref(null);

// ── Derivados ──────────────────────────────────────────────
const tieneTelefono = computed(() => !!pedido.value?.cliente_telefono);

// Los clientes de venta al público no quieren factura (Alondra): se oculta
// la pestaña entera en vez de solo deshabilitar el botón de enviar.
const pestanas = computed(() => {
  const base = [
    { key: "factura", label: "Factura", icon: "bi bi-file-earmark-pdf" },
    { key: "guias", label: "Guías", icon: "bi bi-truck", contador: guias.value.length },
    { key: "notas", label: "Notas", icon: "bi bi-chat-left-text" },
    {
      key: "proyectos",
      label: "Proyectos",
      icon: "bi bi-folder2-open",
      contador: proyectosGuardados.value.length,
    },
  ];
  return pedido.value?.venta_publico ? base.filter((t) => t.key !== "factura") : base;
});

const hayFacturaCargada = computed(
  () => !!(pedido.value?.factura_pdf || facturaArchivo.value)
);

// Un envío en tránsito bloquea el botón: reenviar mientras WhatsApp aún no
// responde solo genera mensajes duplicados al cliente.
const facturaEnTransito = computed(() => enTransito(ultimaFactura.value));
const guiasEnTransito = computed(() => enTransito(ultimasGuias.value));

const puedeMandarFactura = computed(
  () => tieneTelefono.value && hayFacturaCargada.value && !enviandoFactura.value && !facturaEnTransito.value
);
const puedeNotificar = computed(
  () => tieneTelefono.value && guias.value.length > 0 && !enviandoNotificacion.value && !guiasEnTransito.value
);

const motivoBloqueoFactura = computed(() => {
  if (!tieneTelefono.value) return "El cliente no tiene teléfono registrado";
  if (!hayFacturaCargada.value) return "Primero adjunta el PDF de la factura";
  if (facturaEnTransito.value) return "Hay un envío en curso, espera la confirmación";
  return "Mandar la factura por WhatsApp";
});

const motivoBloqueoGuias = computed(() => {
  if (!tieneTelefono.value) return "El cliente no tiene teléfono registrado";
  if (!guias.value.length) return "Primero registra al menos una guía";
  if (guiasEnTransito.value) return "Hay un envío en curso, espera la confirmación";
  return "Mandar las guías por WhatsApp";
});

const textoBotonFactura = computed(() => {
  if (enviandoFactura.value) return "Enviando...";
  if (fallo(ultimaFactura.value)) return "Reintentar";
  return ultimaFactura.value ? "Volver a enviar" : "Enviar factura";
});

const textoBotonGuias = computed(() => {
  if (enviandoNotificacion.value) return "Enviando...";
  if (fallo(ultimasGuias.value)) return "Reintentar";
  return ultimasGuias.value ? "Volver a enviar" : "Notificar cliente";
});

// ── Ciclo de vida ──────────────────────────────────────────
onMounted(() => {
  modalInstance = new Modal(el.value);
  el.value.addEventListener("hidden.bs.modal", alCerrar);
});

async function abrir(p) {
  pedido.value = { ...p };
  pestanaActiva.value = p.venta_publico ? "guias" : "factura";

  facturaArchivo.value = null;
  errorFactura.value = "";
  errorNotificacion.value = "";
  nuevaPaqueteria.value = null;
  nuevoNumeroGuia.value = "";
  abiertoPaq.value = false;

  guias.value = p.guias ? [...p.guias] : [];
  notas.value = p.notas || "";
  notasOriginales.value = p.notas || "";
  proyectosGuardados.value = (p.proyectos || []).map((x) => ({ ...x, _preview: false }));
  proyectosNuevos.value = [];

  modalInstance.show();

  cargarPaqueterias();
  cargarMensajes(p.numero_pedido);
}
defineExpose({ abrir });

function alCerrar() {
  // Soltar la suscripción del WebSocket al cerrar: si el usuario no está
  // viendo el modal no hay por qué mantener el socket abierto.
  limpiarMensajes();
  proyectosNuevos.value.forEach((p) => p._objectUrl && URL.revokeObjectURL(p._objectUrl));
  proyectosNuevos.value = [];
}

// ── Directiva click-outside ────────────────────────────────
const vClickOutside = {
  mounted(elemento, binding) {
    elemento._co = (e) => {
      if (!elemento.contains(e.target)) binding.value(e);
    };
    document.addEventListener("click", elemento._co);
  },
  unmounted(elemento) {
    document.removeEventListener("click", elemento._co);
  },
};

// ── Factura ────────────────────────────────────────────────
function onSeleccionarFactura(e) {
  const file = e.target.files?.[0];
  if (!file) return;
  facturaArchivo.value = file;
  errorFactura.value = "";
}

async function handleEnviarFactura() {
  enviandoFactura.value = true;
  errorFactura.value = "";
  try {
    // Si hay PDF nuevo se sube primero: la tarea de envío lee el archivo
    // que ya esté guardado en el pedido.
    if (facturaArchivo.value) {
      const resultado = await subirFacturaPedido(
        pedido.value.numero_pedido,
        facturaArchivo.value
      );
      if (resultado?.factura_pdf) {
        pedido.value = { ...pedido.value, factura_pdf: resultado.factura_pdf };
      }
      facturaArchivo.value = null;
      emit("guardado");
    }

    await enviarFacturaPedido(pedido.value.numero_pedido);
    // A partir de aquí el estado lo dicta el webhook, no esta petición:
    // el registro aparece por WebSocket en cuanto la tarea llama a Meta.
    await cargarMensajes(pedido.value.numero_pedido);
  } catch (err) {
    errorFactura.value =
      err.response?.data?.detail || "No se pudo encolar el envío de la factura.";
  } finally {
    enviandoFactura.value = false;
  }
}

// ── Guías ──────────────────────────────────────────────────
async function cargarPaqueterias() {
  if (paqueterias.value.length > 0) return;
  cargandoPaqueterias.value = true;
  try {
    paqueterias.value = await getPaqueterias();
  } catch (e) {
    console.error("Error cargando paqueterías:", e);
  } finally {
    cargandoPaqueterias.value = false;
  }
}

function cerrarDropdownPaq() {
  abiertoPaq.value = false;
}
function seleccionarPaqueteria(paq) {
  nuevaPaqueteria.value = paq;
  abiertoPaq.value = false;
}

async function handleAgregarGuia() {
  if (!nuevaPaqueteria.value || !nuevoNumeroGuia.value.trim()) return;
  agregandoGuia.value = true;
  try {
    const guia = await agregarGuiaPedido(pedido.value.numero_pedido, {
      paqueteria_id: nuevaPaqueteria.value.id,
      numero_guia: nuevoNumeroGuia.value.trim(),
    });
    guias.value.push(guia);
    nuevoNumeroGuia.value = "";
    nuevaPaqueteria.value = null;
    emit("guardado");
  } catch (err) {
    Swal.fire("Error", err.response?.data?.detail || "No se pudo agregar la guía.", "error");
  } finally {
    agregandoGuia.value = false;
  }
}

async function handleEliminarGuia(guiaId) {
  eliminandoGuia.value = guiaId;
  try {
    await eliminarGuiaPedido(pedido.value.numero_pedido, guiaId);
    guias.value = guias.value.filter((g) => g.id !== guiaId);
    emit("guardado");
  } catch (err) {
    Swal.fire("Error", err.response?.data?.detail || "No se pudo eliminar la guía.", "error");
  } finally {
    eliminandoGuia.value = null;
  }
}

async function handleNotificarEnvio() {
  enviandoNotificacion.value = true;
  errorNotificacion.value = "";
  try {
    await notificarEnvioPedido(pedido.value.numero_pedido);
    await cargarMensajes(pedido.value.numero_pedido);
  } catch (err) {
    errorNotificacion.value =
      err.response?.data?.detail || "No se pudo encolar la notificación.";
  } finally {
    enviandoNotificacion.value = false;
  }
}

// ── Notas ──────────────────────────────────────────────────
async function handleGuardarNotas() {
  guardandoNotas.value = true;
  try {
    await actualizarPedido(pedido.value.numero_pedido, { notas: notas.value });
    notasOriginales.value = notas.value;
    emit("guardado");
    Swal.fire({
      icon: "success",
      title: "Notas guardadas",
      timer: 1400,
      showConfirmButton: false,
    });
  } catch (err) {
    Swal.fire("Error", err.response?.data?.detail || "No se pudieron guardar las notas.", "error");
  } finally {
    guardandoNotas.value = false;
  }
}

// ── Proyectos ──────────────────────────────────────────────
function handleChangeProyectos(e) {
  agregarArchivosProyecto([...e.target.files]);
  e.target.value = "";
}
function handleDropProyectos(e) {
  arrastrando.value = false;
  agregarArchivosProyecto(
    [...e.dataTransfer.files].filter((f) => f.type === "application/pdf")
  );
}
function agregarArchivosProyecto(archivos) {
  const yaPendientes = proyectosNuevos.value.map((p) => p.archivo.name);
  const sinDuplicados = archivos.filter((f) => !yaPendientes.includes(f.name));
  proyectosNuevos.value.push(
    ...sinDuplicados.map((archivo) => ({
      archivo,
      _objectUrl: URL.createObjectURL(archivo),
    }))
  );
}
function quitarProyectoNuevo(idx) {
  const lista = proyectosNuevos.value;
  if (lista[idx]._objectUrl) URL.revokeObjectURL(lista[idx]._objectUrl);
  lista.splice(idx, 1);
}

async function handleSubirProyectos() {
  subiendoProyectos.value = true;
  try {
    const resultados = await Promise.allSettled(
      proyectosNuevos.value.map((p) => {
        const fd = new FormData();
        fd.append("archivo", p.archivo);
        return agregarProyectoPedido(pedido.value.numero_pedido, fd);
      })
    );

    const exitosos = [];
    const fallidos = [];
    resultados.forEach((r, idx) => {
      if (r.status === "fulfilled") {
        exitosos.push({ ...(r.value.data ?? r.value), _preview: false });
      } else {
        fallidos.push(proyectosNuevos.value[idx].archivo.name);
      }
    });

    proyectosNuevos.value
      .filter((_, i) => resultados[i].status === "fulfilled")
      .forEach((p) => p._objectUrl && URL.revokeObjectURL(p._objectUrl));

    proyectosGuardados.value.push(...exitosos);
    proyectosNuevos.value = proyectosNuevos.value.filter(
      (_, i) => resultados[i].status === "rejected"
    );

    if (fallidos.length > 0) {
      Swal.fire("Error", `No se pudieron subir: ${fallidos.join(", ")}`, "error");
    }
    emit("guardado");
  } finally {
    subiendoProyectos.value = false;
  }
}

async function handleEliminarProyecto(proyectoId) {
  eliminandoProyecto.value = proyectoId;
  try {
    await eliminarProyectoPedido(pedido.value.numero_pedido, proyectoId);
    proyectosGuardados.value = proyectosGuardados.value.filter((p) => p.id !== proyectoId);
    emit("guardado");
  } catch (err) {
    Swal.fire("Error", err.response?.data?.detail || "No se pudo eliminar el proyecto.", "error");
  } finally {
    eliminandoProyecto.value = null;
  }
}

// ── Helpers ────────────────────────────────────────────────
function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function nombreArchivo(ruta) {
  return String(ruta).split("?")[0].split("/").pop();
}
</script>

<style scoped>
.entrega-modal-dialog {
  display: flex;
  align-items: center;
  min-height: calc(100% - 1rem);
  margin: 0.5rem auto;
}
/* Hasta 991.98px el menú es un navbar fijo de 65px (SidebarGeneral.vue).
   Sin este margen superior el modal se mete por debajo y el encabezado
   queda tapado. 73px = 65 del navbar + 8 de aire. */
@media (max-width: 991.98px) {
  .entrega-modal-dialog {
    align-items: flex-start;
    margin-top: 73px;
    margin-bottom: 8px;
    min-height: unset;
    width: calc(100% - 16px);
    max-width: 100%;
  }
}
.entrega-modal {
  background-color: var(--d-cream);
  border: 1.5px solid rgba(64, 34, 8, 0.2);
  border-radius: 18px;
}
.modal-header {
  padding: 1.1rem 1.4rem 0.6rem;
}
.titulo-modal {
  font-size: 1.05rem;
}
.cliente-nombre {
  font-size: 0.82rem;
  margin-top: 2px;
}
.badge-cliente-num {
  background: var(--d-green-dark);
  color: var(--d-cream);
  font-family: var(--d-font-mono);
  font-size: 0.72rem;
  font-weight: 700;
  border-radius: 4px;
  padding: 1px 6px;
}
.tel-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-left: 8px;
  font-size: 0.72rem;
  font-weight: 700;
  border-radius: 20px;
  padding: 1px 8px;
}
.tel-chip.ok {
  color: #2e7d32;
  background: rgba(46, 125, 50, 0.1);
}
.tel-chip.falta {
  color: #c62828;
  background: rgba(198, 40, 40, 0.1);
}

/* ── Aviso sin teléfono ─────────────────────────────────── */
.aviso-sin-telefono {
  display: flex;
  align-items: flex-start;
  background: rgba(198, 40, 40, 0.07);
  border: 1px solid rgba(198, 40, 40, 0.25);
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 0.8rem;
  color: #c62828;
  margin-bottom: 12px;
}

/* ── Pestañas ───────────────────────────────────────────── */
.nav-tabs-custom {
  display: flex;
  gap: 6px;
  list-style: none;
  padding: 0;
  margin: 0 0 1rem;
  border-bottom: 1px solid rgba(64, 34, 8, 0.12);
}
.tab-btn {
  background: none;
  border: none;
  border-bottom: 2.5px solid transparent;
  padding: 6px 12px;
  font-size: 0.85rem;
  font-weight: 700;
  color: rgba(64, 34, 8, 0.5);
  cursor: pointer;
  transition: all 0.15s;
}
.tab-btn:hover {
  color: var(--d-brown);
}
.tab-btn.active {
  color: var(--d-green-dark);
  border-bottom-color: var(--d-green-dark);
}
.tab-contador {
  display: inline-block;
  margin-left: 5px;
  background: var(--d-green-dark);
  color: var(--d-cream);
  font-size: 0.68rem;
  border-radius: 10px;
  padding: 0 6px;
}

/* ── Bloques ────────────────────────────────────────────── */
.bloque {
  background: white;
  border: 1px solid rgba(96, 108, 56, 0.22);
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 12px;
}
.bloque-header {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--d-brown);
  margin-bottom: 10px;
}
.badge-count {
  background: var(--d-green-dark);
  color: var(--d-cream);
  font-size: 0.68rem;
  border-radius: 10px;
  padding: 0 7px;
}

/* ── Filas de acción ────────────────────────────────────── */
.accion-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.archivo-zona-wrap {
  flex: 1;
  min-width: 200px;
}
.archivo-zona {
  border: 1.5px dashed rgba(96, 108, 56, 0.45);
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 0.82rem;
  color: var(--d-brown);
  cursor: pointer;
  transition: border-color 0.15s;
}
.archivo-zona:hover {
  border-color: var(--d-green-dark);
}

.btn-accion {
  border: none;
  border-radius: 8px;
  padding: 7px 14px;
  font-size: 0.82rem;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}
.btn-accion:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.btn-wa {
  background: #25d366;
  color: white;
}
.btn-wa:hover:not(:disabled) {
  background: #1da851;
}
.btn-agregar,
.btn-guardar {
  background: var(--d-brown);
  color: var(--d-cream);
}
.btn-agregar:hover:not(:disabled),
.btn-guardar:hover:not(:disabled) {
  background: var(--d-green-dark);
}

.requisitos {
  display: flex;
  gap: 12px;
  flex: 1;
}
.requisito {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}
.requisito.ok {
  color: #2e7d32;
}
.requisito.falta {
  color: rgba(198, 40, 40, 0.75);
}

/* ── Guías ──────────────────────────────────────────────── */
.guias-lista {
  margin-bottom: 10px;
}
.guia-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
  border-bottom: 1px solid rgba(64, 34, 8, 0.07);
}
.guia-badge-paq {
  background: rgba(96, 108, 56, 0.15);
  color: var(--d-green-dark);
  font-size: 0.72rem;
  font-weight: 700;
  border-radius: 4px;
  padding: 1px 7px;
}
.guia-num {
  font-family: var(--d-font-mono);
  font-size: 0.82rem;
  color: var(--d-brown);
  flex: 1;
}
.nueva-guia-wrap {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}
.select-wrapper {
  position: relative;
  min-width: 150px;
}
.paq-display {
  border: 1px solid rgba(96, 108, 56, 0.4);
  border-radius: 8px;
  padding: 7px 12px;
  font-size: 0.82rem;
  background: white;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}
.paq-display.placeholder {
  color: rgba(64, 34, 8, 0.45);
}
.paq-dropdown {
  position: absolute;
  z-index: 20;
  top: calc(100% + 3px);
  left: 0;
  right: 0;
  background: white;
  border: 1px solid rgba(96, 108, 56, 0.35);
  border-radius: 8px;
  max-height: 190px;
  overflow-y: auto;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
}
.paq-item {
  padding: 7px 12px;
  font-size: 0.82rem;
  cursor: pointer;
}
.paq-item:hover,
.paq-item.selected {
  background: rgba(96, 108, 56, 0.12);
}
.guia-input {
  flex: 1;
  min-width: 160px;
  border: 1px solid rgba(96, 108, 56, 0.4);
  border-radius: 8px;
  padding: 7px 12px;
  font-size: 0.82rem;
  outline: none;
}
.guia-input:focus {
  border-color: var(--d-green-dark);
}

.btn-quitar,
.btn-toggle-preview {
  background: none;
  border: 1px solid rgba(64, 34, 8, 0.2);
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 0.75rem;
  cursor: pointer;
  color: var(--d-brown);
}
.btn-quitar:hover:not(:disabled) {
  border-color: #c62828;
  color: #c62828;
}

/* ── Notas ──────────────────────────────────────────────── */
.notas-textarea {
  width: 100%;
  border: 1px solid rgba(96, 108, 56, 0.4);
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 0.85rem;
  color: var(--d-brown);
  resize: vertical;
  outline: none;
}
.notas-textarea:focus {
  border-color: var(--d-green-dark);
}
.hint,
.sin-envios-hint {
  font-size: 0.75rem;
  color: rgba(64, 34, 8, 0.5);
}
.sin-envios-hint {
  margin: 8px 0 0;
  font-style: italic;
}
.error-local {
  display: flex;
  align-items: flex-start;
  margin-top: 8px;
  font-size: 0.78rem;
  color: #c62828;
}

/* ── Proyectos ──────────────────────────────────────────── */
.upload-zone {
  border: 2px dashed rgba(96, 108, 56, 0.4);
  border-radius: 12px;
  padding: 22px;
  text-align: center;
  cursor: pointer;
  transition: all 0.15s;
}
.upload-zone:hover,
.upload-zone.dragging {
  border-color: var(--d-green-dark);
  background: rgba(96, 108, 56, 0.05);
}
.upload-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
}
.upload-icon {
  font-size: 1.8rem;
  color: var(--d-green);
}
.vacio-container {
  text-align: center;
  padding: 30px 10px;
  color: rgba(64, 34, 8, 0.4);
}
.vacio-container i {
  font-size: 2rem;
}
.proyecto-card {
  background: white;
  border: 1px solid rgba(96, 108, 56, 0.22);
  border-radius: 10px;
  padding: 9px 12px;
  margin-bottom: 8px;
}
.proyecto-card-nuevo {
  border-style: dashed;
}
.proyecto-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.proyecto-nombre {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--d-brown);
  word-break: break-all;
}
.proyecto-size {
  font-size: 0.72rem;
}
.preview-container {
  margin-top: 8px;
}
.pdf-embed {
  width: 100%;
  height: 460px;
  border: 1px solid rgba(64, 34, 8, 0.15);
  border-radius: 8px;
}
.btn-subir-proyectos {
  width: 100%;
  background: var(--d-brown);
  color: var(--d-cream);
  border: none;
  border-radius: 8px;
  padding: 9px;
  font-weight: 700;
  font-size: 0.85rem;
  cursor: pointer;
}
.btn-subir-proyectos:disabled {
  opacity: 0.5;
}

/* ── Footer ─────────────────────────────────────────────── */
.modal-footer {
  border-top: 0.5px solid rgba(64, 34, 8, 0.12) !important;
  padding: 0.8rem 1.4rem;
}
.conexion-hint {
  font-size: 0.72rem;
  color: rgba(64, 34, 8, 0.45);
}
.btn-cerrar {
  background: none;
  border: 1px solid rgba(96, 108, 56, 0.4);
  color: var(--d-green);
  font-weight: 600;
  border-radius: 8px;
  font-size: 0.85rem;
}
.btn-cerrar:hover {
  background: rgba(96, 108, 56, 0.07);
}
</style>
